import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import styled from 'styled-components'
import { CouponCard } from '@/features/shop/components/CouponCard'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { screenSizes } from '@shipt/design-system-themes'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { EmptyState } from '@/features/shop/components/EmptyState'
import { ErrorPage } from '@/features/shared/components/ErrorPage'
import { CategoryFilterSection } from '@/features/shop/components/ProductFilter/CategoryFilterSection'
import { FilterTagSection } from '@/features/shop/components/FilterTagSection'
import { CouponsPageHeader } from '@/features/shop/components/CouponsPage/CouponsPageHeader'
import { FilterSection } from '@/features/shop/components/ProductFilter/FilterSection'
import { useInfiniteQueryCouponsSearch } from '@/features/shop/services/Search/queries'
import { findCategoryFacet } from '@/features/shop/services/Search/utils'
import { Button } from '@shipt/design-system-buttons'
import { getRetailerCouponsUrl } from '@shared/constants/routes'
import { Column, Row } from '@shipt/design-system-layouts'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { useParams } from 'next/navigation'
import { type ParamsOf } from '@next-types/routes'
import { SEO_PAGE_TYPE } from '@/features/shared/components/SEOHeader/types'
import { getCanonicalUrl } from '@/features/shared/utils/seo/seoConfig'
import { getRetailerShopBreadcrumbs } from '@/features/shop/utils/seo'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'

export const CouponsContainer = () => {
  const router = useSegwayRouter()
  const storeName = useUserStoreName()
  const { category_id } = useSearchQueryParams()
  const { data, error, isPending, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQueryCouponsSearch()

  const params = useParams<ParamsOf<'/shop/[store_slug]'>>()
  const canonicalUrl = getCanonicalUrl(`/shop/${params?.store_slug}/coupons`)
  const pageTitle = `${storeName} Coupons, Save on Groceries & Essentials | Shipt`
  const pageDescription = `Find the latest Shipt coupons for ${storeName}. Browse discounted products and see the savings available near you. Shop today!`
  const breadcrumbs = getRetailerShopBreadcrumbs(params?.store_slug, storeName, [
    { name: pageTitle, item: canonicalUrl },
  ])
  const pageTypeSchema = {
    '@context': 'https://schema.org',
    '@type': [SEO_PAGE_TYPE.WEB_PAGE, SEO_PAGE_TYPE.COLLECTION_PAGE],
    name: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    image: {
      url: objectStorageUrl('get-started-hero.png'),
      width: 872,
      height: 1072,
    },
  }
  if (isPending)
    return (
      <Row align="center" justify="center">
        <LoadingIndicator size="xl" />
      </Row>
    )
  if (error) return <ErrorPage />
  const couponListUrl = getRetailerCouponsUrl(storeName)

  const { pages } = data
  const { totalCount: totalCoupons = 0, facets = [] } = pages.at(-1) ?? {}
  const { items = [] } = findCategoryFacet(facets) ?? {}
  const currentCategory = items.find((c) => c.id === Number(category_id))
  const handleOnClearFilters = () => router.push(couponListUrl)

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        breadcrumbs={breadcrumbs}
        pageTypeSchema={pageTypeSchema}
      />
      <PageContainer>
        <CouponsPageHeader
          totalCoupons={totalCoupons}
          currentCategory={currentCategory}
          couponListUrl={couponListUrl}
        />
        {Number(totalCoupons) > 0 ? (
          <ContentWrapper>
            <FilterWrapper>
              <FilterTagSection
                onClick={handleOnClearFilters}
                activeFiltersLength={category_id ? 1 : 0}
              />
              <FilterSection title="Categories">
                <CategoryFilterSection items={items} expandAllCategories />
              </FilterSection>
            </FilterWrapper>
            {isFetching && !isFetchingNextPage ? (
              <Row align="center" justify="center" marginTop="5xl">
                <LoadingIndicator size="md" />
              </Row>
            ) : (
              <Column spacing="4xl" align="center">
                <Row wrap justify={{ base: 'center', md: 'start' }}>
                  {pages
                    .flatMap((page) => page.coupons)
                    .map((coupon, index) => (
                      <CouponCard index={index} key={coupon.couponId} coupon={coupon} />
                    ))}
                </Row>
                {!!hasNextPage && (
                  <Button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    loading={isFetchingNextPage}
                    variant="secondary"
                  >
                    Show more
                  </Button>
                )}
              </Column>
            )}
          </ContentWrapper>
        ) : (
          <CouponsEmptyState
            text="No coupons available"
            subText="New coupons will appear here"
            {...(category_id && {
              buttonText: 'Go back to all coupons',
              emptyStateRoute: couponListUrl,
            })}
          />
        )}
      </PageContainer>
    </>
  )
}

const PageContainer = styled(GridLayoutContainer)`
  margin-top: 16px;
`

const FilterWrapper = styled.div`
  display: none;

  @media ${screenSizes.tablet} {
    display: block;
    max-width: 15rem;
    min-width: 15rem;
    margin-right: 2rem;
  }
`

const ContentWrapper = styled.div`
  margin-left: 16px;

  @media ${screenSizes.tablet} {
    display: flex;
    align-items: flex-start;
    margin-left: 0;
  }
`

const CouponsEmptyState = styled(EmptyState)`
  width: 100%;
`
